// A timelapse walk played flat (#17, Phelipe 2026-09-25: "the walk is a
// little choppy, it's not fluid"): when the video moves on to the next
// picture, the last one stays on top for a moment and fades away while it
// grows a little - the same step forward the 360° view glides
// (lib/panorama_video.js), drawn on a 2D canvas laid over the <video>.
//
//   const fade = new PictureFade(video, canvas)
//   fade.start(frameSeconds) ... fade.stop()

const FADE_MS = 300 // with the 360° glide (Phelipe 2026-09-25, was 450)
const GROW = 1.06 // how much the last picture grows as it fades: a step forward
const LONGEST_SIDE = 1280 // the kept copy of the last picture needn't be full size

export default class PictureFade {
  constructor(video, canvas) {
    this.video = video
    this.canvas = canvas
    this.frameSeconds = 0
    this.index = null
    this.kept = null
    this.running = false
  }

  start(frameSeconds) {
    this.stop()
    this.frameSeconds = Number(frameSeconds) > 0 ? Number(frameSeconds) : 0
    if (!this.frameSeconds) return
    this.running = true
    this.index = null
    const video = this.video
    this._onLanded = () => this._shown(video.currentTime || 0)
    for (const type of ["seeked", "timeupdate", "loadeddata"]) video.addEventListener(type, this._onLanded)
    if (typeof video.requestVideoFrameCallback === "function") {
      const presented = (_now, meta) => {
        if (!this.running) return
        this._shown(Number.isFinite(meta?.mediaTime) ? meta.mediaTime : video.currentTime || 0)
        this._handle = video.requestVideoFrameCallback(presented)
      }
      this._handle = video.requestVideoFrameCallback(presented)
    }
  }

  stop() {
    this.running = false
    const video = this.video
    if (this._onLanded) {
      for (const type of ["seeked", "timeupdate", "loadeddata"]) video.removeEventListener(type, this._onLanded)
      this._onLanded = null
    }
    if (this._handle != null) video.cancelVideoFrameCallback?.(this._handle)
    this._handle = null
    this.canvas.style.opacity = "0"
    this.index = null
  }

  // The video shows the frame at `mediaTime`: a new picture fades in over the last one.
  _shown(mediaTime) {
    if (!this.running || this.video.readyState < 2) return
    const index = Math.floor(mediaTime / this.frameSeconds + 1e-3)
    if (index === this.index) return
    const step = this.index == null ? 0 : index - this.index
    this.index = index
    if (step !== 0 && this.kept) this._fadeOut(this.kept, step > 0)
    this.kept = this._keep()
  }

  // A small copy of the picture on screen now, for when the next one arrives.
  _keep() {
    const { video } = this
    const width = video.videoWidth
    const height = video.videoHeight
    if (!width || !height) return null
    const scale = Math.min(1, LONGEST_SIDE / Math.max(width, height))
    const copy = this.kept || document.createElement("canvas")
    copy.width = Math.round(width * scale)
    copy.height = Math.round(height * scale)
    const ctx = copy.getContext?.("2d")
    if (!ctx) return null
    ctx.drawImage(video, 0, 0, copy.width, copy.height)
    return copy
  }

  // The last picture over the video, placed where the video draws (it keeps
  // its shape inside the stage), then faded away while it grows (or shrinks
  // for a step back).
  _fadeOut(picture, forward) {
    const canvas = this.canvas
    const ctx = canvas.getContext?.("2d")
    if (!ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr))
    canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr))
    const fit = Math.min(canvas.width / picture.width, canvas.height / picture.height)
    const w = picture.width * fit
    const h = picture.height * fit
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(picture, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h)
    // Copy it: the kept picture is drawn over with the new one straight after.
    canvas.style.transition = "none"
    canvas.style.opacity = "1"
    canvas.style.transform = "scale(1)"
    void canvas.offsetWidth // start the fade from here
    canvas.style.transition = `opacity ${FADE_MS}ms ease-out, transform ${FADE_MS}ms ease-out`
    canvas.style.opacity = "0"
    canvas.style.transform = `scale(${forward ? GROW : 1 / GROW})`
  }
}
